import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import {
  NbMediaBreakpointsService,
  NbMenuItem,
  NbMenuService,
  NbSidebarService,
  NbThemeService,
  NbToastrService,
  NbWindowService
} from "@nebular/theme";
import { LayoutService } from "../../../@core/utils";
import { filter, map, takeUntil } from "rxjs/operators";
import { Subject, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import { NbAuthJWTToken, NbAuthService } from "@nebular/auth";
import { Router } from "@angular/router";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { User } from "../../../models/user.model";
import { UserEditComponent } from "../../../pages/cms/manage-users/user-edit/user-edit.component";
import { UserService } from "../../../services/user.service";

@Component({
  selector: "ngx-header",
  styleUrls: ["./header.component.scss"],
  templateUrl: "./header.component.html",
})
export class HeaderComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  userPictureOnly: boolean = false;
  user: any;

  themes = [
    {
      value: "default",
      name: "Light",
    },
    {
      value: "dark",
      name: "Dark",
    },
    {
      value: "cosmic",
      name: "Cosmic",
    },
    {
      value: "corporate",
      name: "Corporate",
    },
  ];

  currentTheme = "default";

  userMenu: NbMenuItem[] = [];

  constructor(
    private sidebarService: NbSidebarService,
    private menuService: NbMenuService,
    private themeService: NbThemeService,
    private authService: NbAuthService,
    private layoutService: LayoutService,
    private breakpointService: NbMediaBreakpointsService,
    private router: Router,
    private translate: TranslateService,
    private windowService: NbWindowService,
    private userService: UserService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.currentTheme = this.themeService.currentTheme;
    this.updateContextMenu();
    
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.updateContextMenu();
    });

    this.authService.onTokenChange()
    .pipe(
      switchMap((token: NbAuthJWTToken) => {
        if (token.isValid()) {
          return this.userService.getCurrentUser();
        } else {
          return of(null);
        }
      }),
      takeUntil(this.destroy$)
    )
    .subscribe((user) => {
      if (user) {
        this.user = user;
      } else {
        this.user = { name: null, photo: null };
      }
    });

    const { xl } = this.breakpointService.getBreakpointsMap();
    this.themeService
      .onMediaQueryChange()
      .pipe(
        map(([, currentBreakpoint]) => currentBreakpoint.width < xl),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (isLessThanXl: boolean) => (this.userPictureOnly = isLessThanXl)
      );

    this.themeService
      .onThemeChange()
      .pipe(
        map(({ name }) => name),
        takeUntil(this.destroy$)
      )
      .subscribe((themeName) => (this.currentTheme = themeName));

    this.menuService
      .onItemClick()
      .pipe(
        filter(({ tag }) => tag === "user-context-menu"),
        map(({ item: {data} }) => data),
        takeUntil(this.destroy$)
      )
      .subscribe((action) => {
        switch (action) {
          case "logout":
            this.authService.logout("email").subscribe((res) => {
              localStorage.removeItem('auth_app_token');
              setTimeout(() => {
                this.router.navigate(["/auth"]);
              })
            }, (error) => {
              console.log(error);
              localStorage.removeItem('auth_app_token');
              setTimeout(() => {
                this.router.navigate(["/auth"]);
              })
            });
            break;
          case "account":
            this.userService.getCurrentUser().subscribe((user: User) => {
              this.openUserEdit(user);
            })
            break;
          default:
            console.log("Not defined");
            break;
        }
      });
  }

  updateContextMenu(): void {
    this.translate.get(['UTILS.LABELS.ACCOUNT', 'UTILS.LABELS.LOGOUT']).subscribe(translations => {
      this.userMenu = [
        { title: translations['UTILS.LABELS.ACCOUNT'], data: 'account' },
        { title: translations['UTILS.LABELS.LOGOUT'], data: 'logout' }
      ];
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeTheme(themeName: string) {
    this.themeService.changeTheme(themeName);
    localStorage.setItem('themeName', themeName);
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, "menu-sidebar");
    this.layoutService.changeLayoutSize();

    return false;
  }

  openUserEdit(user: User) {
		let title: string = this.translate.instant("USERS.EDIT_USER");
		this.windowService.open(UserEditComponent, {
			title: title,
			context: {
				user: user,
				add: false,
        profile: true
			},
      closeOnBackdropClick: false,
      buttons: {
				minimize: false,
				maximize: false,
				fullScreen: false,
			}
		}).onClose.subscribe((res: { user: User; operation: string; }) => {
      if (res) {
        console.log(res);
        this.user = res.user
      }
    })
	}

  navigateHome() {
    this.menuService.navigateHome();
    return false;
  }
}

